import React from 'react';

import { EditOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Dropdown as AntDDropdown } from 'antd';
import styled from 'styled-components';

import DownCaretIcon from 'common/Icons/DownCaretIcon';
import Button from 'components/lib/Button';
import { ActionPlanListItemType } from 'types/actionPlans';

import {
  getActionPlanActorName,
  getActionPlanSourceLabel,
  isAIGeneratedValue,
  isEditedEntity,
} from './actionPlanMetaUtils';

const ActionPlansActionsDropdownMenu = styled.div`
  .ant-dropdown-menu {
    padding: 0;
    border-radius: 0;
    border: 1px solid var(--line-gray);
    border-bottom: 0;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
    min-width: 240px;
    width: max-content;
  }

  .ant-dropdown-menu-item {
    font-size: 14px;
    padding: 6px 10px;
    border-bottom: 1px solid var(--line-gray);
    white-space: nowrap;
  }
`;

const TitleCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  min-width: 0;
  padding-left: 10px;
`;

const TitleButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  padding: 0;
  text-align: left;
`;

const TitleIcon = styled.img`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
`;

const TitleTextContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-width: 100%;
`;

const PlanTitleText = styled.span`
  font-family: var(--font-bold);
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SourceText = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
  font-family: var(--font-regular);
`;

const SourceMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ModifiedText = styled.span`
  font-size: 11px;
  color: var(--text-secondary);
  font-family: var(--font-bold);
  display: inline-flex;
  align-items: center;
  gap: 3px;
`;

const ActionsCell = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-right: 20px;
`;

type TitleProps = {
  title: string;
  plan: ActionPlanListItemType;
  fallbackUserLabel: string;
  onViewPlan: (planId: number) => void;
};

export const ActionPlanTitleCell = ({ title, plan, fallbackUserLabel, onViewPlan }: TitleProps) => {
  const aiGenerated = isAIGeneratedValue(plan.is_ai_generated);
  const createdByName = getActionPlanActorName(plan.created_by);
  const sourceLabel = getActionPlanSourceLabel(
    aiGenerated,
    createdByName,
    fallbackUserLabel,
    plan.source,
    plan.source_insights,
    plan.related_alert_type_id
  );
  const isEdited = isEditedEntity(plan);

  return (
    <TitleCell data-test-id="action-plans-table-plan-title-cell">
      <TitleButton type="button" onClick={() => onViewPlan(plan.id)}>
        {aiGenerated ? (
          <TitleIcon src="/icons/ai-blob.svg" alt="ai-blob" />
        ) : (
          <TitleIcon src="/icons/people-black.svg" alt="people-black" />
        )}
        <TitleTextContent>
          <PlanTitleText data-test-id="action-plans-table-plan-title">{title}</PlanTitleText>
          <SourceMetaRow>
            <SourceText data-test-id="action-plans-table-plan-source">{sourceLabel}</SourceText>
            {isEdited && (
              <ModifiedText data-test-id="action-plans-table-plan-modified">
                <EditOutlined />
                Edited
              </ModifiedText>
            )}
          </SourceMetaRow>
        </TitleTextContent>
      </TitleButton>
    </TitleCell>
  );
};

type ActionsProps = {
  plan: ActionPlanListItemType;
  selectedCommunityId?: number;
  showRelatedAlert: boolean;
  onViewPlan: (planId: number) => void;
  onEditPlan: (plan: ActionPlanListItemType) => void;
  onDeletePlan: (plan: ActionPlanListItemType) => void;
  onViewAlert: (communityId: number, alertId: number) => void;
};

export const ActionPlanActionsCell = ({
  plan,
  selectedCommunityId,
  showRelatedAlert,
  onViewPlan,
  onEditPlan,
  onDeletePlan,
  onViewAlert,
}: ActionsProps) => {
  const actionItems: MenuProps['items'] = [
    {
      key: 'view-plan',
      label: <span style={{ whiteSpace: 'nowrap' }}>View Plan and Action Items</span>,
      onClick: () => {
        onViewPlan(plan.id);
      },
    },
    {
      key: 'edit-plan',
      label: 'Edit Plan',
      onClick: () => {
        onEditPlan(plan);
      },
    },
    {
      key: 'delete-plan',
      label: 'Delete Plan',
      onClick: () => {
        onDeletePlan(plan);
      },
    },
    ...(showRelatedAlert && plan.related_alert_id
      ? [
          {
            key: 'view-alert',
            label: 'View Related Alert',
            onClick: () => {
              if (!selectedCommunityId || !plan.related_alert_id) {
                return;
              }
              onViewAlert(selectedCommunityId, plan.related_alert_id);
            },
          },
        ]
      : []),
  ];

  return (
    <ActionsCell className="actions-hover-reveal">
      <AntDDropdown
        dropdownRender={(menu) => <ActionPlansActionsDropdownMenu>{menu}</ActionPlansActionsDropdownMenu>}
        menu={{ items: actionItems }}
      >
        <Button style={{ width: 100 }} data-test-id={`action-plans-actions-button-${plan.id}`}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>Actions</span>
            <DownCaretIcon />
          </div>
        </Button>
      </AntDDropdown>
    </ActionsCell>
  );
};
